import * as THREE from 'three';
import { getLayoutHeight } from './Grid3DLayout.js';

type ViewMode = 'angled' | 'top';

export interface ViewportInsets {
  top: number;
  bottom: number;
}

const DEFAULT_INSETS: ViewportInsets = { top: 64, bottom: 88 };

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  private viewMode: ViewMode = 'angled';
  private lookTargetY = 0;
  private screenBiasY = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(42, aspect, 0.1, 100);
    this.camera.position.set(0, 7, 7);
    this.camera.lookAt(0, 0, 0);
  }

  fitToBoard(
    boardSize: number,
    viewportWidth: number,
    viewportHeight: number,
    insets: ViewportInsets = DEFAULT_INSETS,
  ): void {
    this.camera.aspect = viewportWidth / viewportHeight;
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const halfTan = Math.tan(fovRad / 2);

    const margin = 0.35;
    const horizontalExtent = boardSize + margin;
    const verticalExtent = boardSize + getLayoutHeight() * 0.85 + margin;

    const aspect = viewportWidth / viewportHeight;
    const insetReserve = (insets.top + insets.bottom) / viewportHeight;
    const fillRatio = aspect < 0.85 ? 0.88 - insetReserve * 0.15 : 0.84 - insetReserve * 0.12;

    const distanceForWidth = horizontalExtent / (2 * halfTan * aspect);
    const distanceForHeight = verticalExtent / (2 * halfTan);
    const distance = Math.max(distanceForWidth, distanceForHeight) / Math.max(fillRatio, 0.72);

    this.lookTargetY = getLayoutHeight() * 0.12;
    this.screenBiasY = ((insets.bottom - insets.top) / viewportHeight) * distance * 0.72;

    this.applyView(distance);
    this.camera.updateProjectionMatrix();
  }

  resize(
    viewportWidth: number,
    viewportHeight: number,
    boardSize: number,
    insets?: ViewportInsets,
  ): void {
    this.fitToBoard(boardSize, viewportWidth, viewportHeight, insets);
  }

  cycleViewMode(
    boardSize: number,
    viewportWidth: number,
    viewportHeight: number,
    insets?: ViewportInsets,
  ): void {
    this.viewMode = this.viewMode === 'angled' ? 'top' : 'angled';
    this.fitToBoard(boardSize, viewportWidth, viewportHeight, insets);
  }

  private applyView(distance: number): void {
    if (this.viewMode === 'top') {
      this.camera.position.set(0, distance * 1.45 + this.lookTargetY, 0.001);
    } else {
      const tilt = 0.82;
      this.camera.position.set(0, distance * tilt + this.lookTargetY * 0.65, distance * 0.92);
    }

    this.camera.position.y += this.screenBiasY;
    this.camera.lookAt(0, this.lookTargetY, 0);
  }
}
